'use client';

import type { ReactNode } from 'react';
import type { CardRenderContext } from '../../tool-config-types';
import type { AttackerAwaitingOutput, AttackerToolOutput } from './types';
import { attackerHitlConfig } from './hitl-config';
import { renderAttackerContent } from './attacker-content-renderer';
import { AttackerResultView } from './attacker-result-renderer';
import { AttackerExecutionTrackingRow } from './AttackerExecutionTrackingRow';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';

function hasNeoAwaitingOutput(
  output: unknown,
): output is AttackerAwaitingOutput {
  return (
    typeof output === 'object' &&
    output !== null &&
    (output as AttackerAwaitingOutput).awaitingCompletion === true &&
    typeof (output as AttackerAwaitingOutput).neoThreadId === 'string'
  );
}

function isExecutionStillPending({
  toolCallId,
  getBackgroundExecutionByToolCallId,
}: {
  toolCallId?: string;
  getBackgroundExecutionByToolCallId?: CardRenderContext['getBackgroundExecutionByToolCallId'];
}): boolean {
  if (!toolCallId || !getBackgroundExecutionByToolCallId) {
    return false;
  }

  return getBackgroundExecutionByToolCallId(toolCallId) !== undefined;
}

function isActiveNeoTrackingOutput(
  output: unknown,
  status: string,
): output is AttackerAwaitingOutput {
  if (status === 'error' || status === 'aborted' || status !== 'in-progress') {
    return false;
  }

  if (typeof output !== 'object' || output === null) {
    return false;
  }

  const record = output as AttackerAwaitingOutput & {
    success?: unknown;
  };

  if (typeof record.neoThreadId !== 'string') {
    return false;
  }

  return record.success === undefined;
}

function isAbortedAwaitingOutput(
  output: unknown,
  status: string,
): output is AttackerAwaitingOutput {
  return (
    status === 'aborted' &&
    typeof output === 'object' &&
    output !== null &&
    typeof (output as AttackerAwaitingOutput).neoThreadId === 'string'
  );
}

function isDetachedLaunch(
  output: unknown,
  status: string,
): output is AttackerToolOutput & {
  threadId: string;
} {
  return (
    status === 'completed' &&
    typeof output === 'object' &&
    output !== null &&
    (output as AttackerToolOutput).mode === 'detached' &&
    typeof (output as AttackerToolOutput).threadId === 'string'
  );
}

function readInputSignalId(input: unknown): string | undefined {
  if (typeof input !== 'object' || input === null) {
    return undefined;
  }

  const signalId = (input as { signalId?: unknown }).signalId;

  return typeof signalId === 'string' && signalId.length > 0
    ? signalId
    : undefined;
}

export function renderAttackerCard(ctx: CardRenderContext): ReactNode {
  const {
    toolCallData,
    onOpenObject,
    getBackgroundExecutionByToolCallId,
    toolName,
  } = ctx;
  const { output, status } = toolCallData;
  const inputSignalId = readInputSignalId(toolCallData.input);

  if (isActiveNeoTrackingOutput(output, status)) {
    const isRunning =
      hasNeoAwaitingOutput(output) &&
      isExecutionStillPending({
        toolCallId: toolCallData.toolCallId,
        getBackgroundExecutionByToolCallId,
      });

    return (
      <AttackerExecutionTrackingRow
        executionId={output.neoThreadId}
        signalId={output.signalId ?? inputSignalId}
        signalTitle={output.signalTitle}
        isRunning={isRunning}
        onOpenObject={onOpenObject}
        headerActions={getHITLArtifactHeaderActions(ctx)}
      />
    );
  }

  if (isAbortedAwaitingOutput(output, status)) {
    return (
      <AttackerExecutionTrackingRow
        executionId={output.neoThreadId}
        signalId={output.signalId ?? inputSignalId}
        signalTitle={output.signalTitle}
        isRunning={false}
        onOpenObject={onOpenObject}
        headerActions={getHITLArtifactHeaderActions(ctx)}
      />
    );
  }

  if (isDetachedLaunch(output, status)) {
    return (
      <AttackerExecutionTrackingRow
        executionId={output.threadId}
        signalId={output.signalId ?? inputSignalId}
        signalTitle={output.signalTitle}
        isRunning={false}
        onOpenObject={onOpenObject}
        headerActions={getHITLArtifactHeaderActions(ctx)}
      />
    );
  }

  if (status === 'started' && output === undefined) {
    const input = toolCallData.input as Record<string, unknown> | undefined;

    if (input) {
      const extractedData = attackerHitlConfig.extractData(input) as Parameters<
        typeof renderAttackerContent
      >[0]['extractedData'];
      const content = renderAttackerContent({
        args: input,
        extractedData,
        maxHeight: '200px',
      });

      if (content !== null) {
        return (
          <FlatToolCard
            title={toolName}
            headerActions={getHITLArtifactHeaderActions(ctx)}
          >
            {content}
          </FlatToolCard>
        );
      }
    }
  }

  if (
    (status === 'started' || status === 'in-progress') &&
    output === undefined
  ) {
    return (
      <FlatToolCard
        title={toolName}
        isRunning
        headerActions={getHITLArtifactHeaderActions(ctx)}
      />
    );
  }

  return (
    <AttackerResultView
      content={output}
      signalId={inputSignalId}
      onOpenObject={onOpenObject}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    />
  );
}
